import styled, { keyframes } from "styled-components";
import { blueColor, goldColor } from "../../../../styled/Theme";
import {
  SafetyCertificateOutlined,
  StarFilled,
  ThunderboltOutlined,
  LockOutlined,
  MobileOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";

const features = [
  { icon: <ThunderboltOutlined />, label: "Instant payments" },
  { icon: <LockOutlined />, label: "256-bit encryption" },
  { icon: <SafetyCertificateOutlined />, label: "YOYA certified" },
  { icon: <MobileOutlined />, label: "Works on any device" },
];

const reviews = [
  {
    name: "Sarah K.",
    text: "Pays my UMEME bill in seconds. Love it.",
    stars: 5,
  },
  {
    name: "James M.",
    text: "Best app for municipal fees. Zero queues.",
    stars: 5,
  },
  { name: "Aisha N.", text: "SACCO payments made so easy now.", stars: 5 },
];

export default function AppDownload() {
  return (
    <Wrapper>
      {/* ── Background noise texture + glow ── */}
      <BgGlowLeft />
      <BgGlowRight />

      <Inner className="container">
        {/* ── Left: copy + badges ── */}
        <LeftCol>
          <Eyebrow
            data-aos="fade-right"
            data-aos-duration="600"
            data-aos-delay="80"
          >
            <EyebrowDot />
            Download the App
          </Eyebrow>

          <Headline
            data-aos="fade-right"
            data-aos-duration="700"
            data-aos-delay="150"
          >
            Your finances,
            <br />
            <AccentGold>one tap away.</AccentGold>
          </Headline>

          <Subline
            data-aos="fade-right"
            data-aos-duration="600"
            data-aos-delay="220"
          >
            Access OnePayX securely on your preferred device. Pay bills, fees,
            parking, and more — all from a single trusted app. Officially
            supported by YOYA Technologies.
          </Subline>

          {/* Feature pills */}
          <FeatureRow
            data-aos="fade-up"
            data-aos-duration="500"
            data-aos-delay="300"
          >
            {features.map((f, i) => (
              <FeaturePill key={i}>
                <FeaturePill>{f.icon}</FeaturePill>
                {f.label}
              </FeaturePill>
            ))}
          </FeatureRow>

          {/* Store buttons */}
          <StoreButtons
            data-aos="fade-up"
            data-aos-duration="600"
            data-aos-delay="380"
          >
            <StoreBtn $primary href="#">
              <StoreBtnIcon>
                {/* Android SVG */}
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="22"
                  height="22"
                >
                  <path d="M17.523 15.341c-.347 0-.63-.283-.63-.63V9.93c0-.347.283-.63.63-.63s.63.283.63.63v4.781c0 .347-.283.63-.63.63zm-11.046 0c-.347 0-.63-.283-.63-.63V9.93c0-.347.283-.63.63-.63s.63.283.63.63v4.781c0 .347-.283.63-.63.63zM8.14 4.513l-.988-1.814a.2.2 0 0 0-.272-.073.2.2 0 0 0-.073.272l1.005 1.846A6.26 6.26 0 0 0 5.5 8.5h13a6.26 6.26 0 0 0-2.312-3.756l1.005-1.846a.2.2 0 0 0-.073-.272.2.2 0 0 0-.272.073l-.988 1.814A6.22 6.22 0 0 0 12 3.5c-.99 0-1.92.24-2.74.666l-.12-.653zM9.5 7a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zm5 0a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1zM5.5 9.5v7a1.5 1.5 0 0 0 1.5 1.5v2.37a.63.63 0 1 0 1.26 0V18h7.48v2.37a.63.63 0 1 0 1.26 0V18a1.5 1.5 0 0 0 1.5-1.5v-7h-13z" />
                </svg>
              </StoreBtnIcon>
              <StoreBtnText>
                <StoreBtnSub>Get it on</StoreBtnSub>
                <StoreBtnMain>Google Play</StoreBtnMain>
              </StoreBtnText>
            </StoreBtn>

            <StoreBtn href="#">
              <StoreBtnIcon>
                {/* Apple SVG */}
                <svg
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  width="22"
                  height="22"
                >
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
              </StoreBtnIcon>
              <StoreBtnText>
                <StoreBtnSub>Download on the</StoreBtnSub>
                <StoreBtnMain>App Store</StoreBtnMain>
              </StoreBtnText>
            </StoreBtn>
          </StoreButtons>

          {/* Rating row */}
          <RatingRow
            data-aos="fade-up"
            data-aos-duration="500"
            data-aos-delay="460"
          >
            <RatingStars>
              {[...Array(5)].map((_, i) => (
                <StarFilled
                  key={i}
                  style={{ color: goldColor, fontSize: 14 }}
                />
              ))}
            </RatingStars>
            <RatingText>
              <RatingScore>4.9</RatingScore>
              <RatingCount>from 12,000+ reviews</RatingCount>
            </RatingText>
            <RatingDivider />
            <RatingBadge>
              <CheckCircleFilled style={{ color: "#22c55e", fontSize: 13 }} />
              Official YOYA Technologies app
            </RatingBadge>
          </RatingRow>
        </LeftCol>

        {/* ── Center: phone mockup ── */}
        <PhoneCol
          data-aos="fade-up"
          data-aos-duration="800"
          data-aos-delay="200"
        >
          <PhoneGlow />
          <PhoneFrame>
            <PhoneNotch />
            <PhoneScreen>
              {/* Mock app UI */}
              <AppHeader>
                <AppHeaderGreet>Good morning 👋</AppHeaderGreet>
                <AppHeaderName>John Mugisha</AppHeaderName>
              </AppHeader>
              <AppBalance>
                <AppBalanceLabel>Wallet Balance</AppBalanceLabel>
                <AppBalanceAmt>UGX 420,000</AppBalanceAmt>
              </AppBalance>
              <AppActions>
                {["Pay Bill", "Taxes", "Parking", "Transfer"].map((a, i) => (
                  <AppAction key={i}>
                    <AppActionDot />
                    <AppActionLabel>{a}</AppActionLabel>
                  </AppAction>
                ))}
              </AppActions>
              <AppTxTitle>Recent Transactions</AppTxTitle>
              <AppTx $color="#22c55e">
                <AppTxIcon>⚡</AppTxIcon>
                <AppTxInfo>
                  <AppTxName>UMEME Bill</AppTxName>
                  <AppTxDate>Today, 10:32 AM</AppTxDate>
                </AppTxInfo>
                <AppTxAmt $out>- UGX 85,000</AppTxAmt>
              </AppTx>
              <AppTx $color={blueColor}>
                <AppTxIcon>🏛</AppTxIcon>
                <AppTxInfo>
                  <AppTxName>KCCA Licence</AppTxName>
                  <AppTxDate>Yesterday</AppTxDate>
                </AppTxInfo>
                <AppTxAmt $out>- UGX 250,000</AppTxAmt>
              </AppTx>
              <AppTx $color={goldColor}>
                <AppTxIcon>🅿</AppTxIcon>
                <AppTxInfo>
                  <AppTxName>Zone A Parking</AppTxName>
                  <AppTxDate>Mon, 14:15</AppTxDate>
                </AppTxInfo>
                <AppTxAmt $out>- UGX 3,000</AppTxAmt>
              </AppTx>
            </PhoneScreen>
          </PhoneFrame>

          {/* Floating notification */}
          <PhoneNotifCard
            data-aos="fade-left"
            data-aos-duration="600"
            data-aos-delay="700"
          >
            <NotifDot />
            <NotifContent>
              <NotifTitle>Payment confirmed</NotifTitle>
              <NotifSub>UGX 85,000 · UMEME</NotifSub>
            </NotifContent>
            <NotifCheck>✓</NotifCheck>
          </PhoneNotifCard>
        </PhoneCol>

        {/* ── Right: review cards ── */}
        <ReviewCol
          data-aos="fade-left"
          data-aos-duration="700"
          data-aos-delay="300"
        >
          <ReviewColLabel>What users say</ReviewColLabel>
          {reviews.map((r, i) => (
            <ReviewCard
              key={i}
              data-aos="fade-up"
              data-aos-delay={380 + i * 100}
              data-aos-duration="580"
            >
              <ReviewStars>
                {[...Array(r.stars)].map((_, j) => (
                  <StarFilled
                    key={j}
                    style={{ color: goldColor, fontSize: 11 }}
                  />
                ))}
              </ReviewStars>
              <ReviewText>"{r.text}"</ReviewText>
              <ReviewName>{r.name}</ReviewName>
            </ReviewCard>
          ))}

          {/* USSD option */}
          <USSDCard
            data-aos="fade-up"
            data-aos-delay="680"
            data-aos-duration="580"
          >
            <USSDIcon>📱</USSDIcon>
            <USSDText>
              <USSDTitle>No smartphone? No problem.</USSDTitle>
              <USSDSub>
                Use USSD <USSDCode>*123#</USSDCode> on any phone
              </USSDSub>
            </USSDText>
          </USSDCard>
        </ReviewCol>
      </Inner>
    </Wrapper>
  );
}

/* ── Animations ── */
const floatPhone = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-10px); }
`;

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateX(12px); }
  to   { opacity: 1; transform: translateX(0); }
`;

/* ── Styles ── */

const Wrapper = styled.section`
  background: linear-gradient(150deg, #07142e 0%, #0d2254 55%, #091a3d 100%);
  padding: 80px 0 88px;
  position: relative;
  overflow: hidden;
`;

const BgGlowLeft = styled.div`
  position: absolute;
  top: -120px;
  left: -120px;
  width: 500px;
  height: 500px;
  border-radius: 50%;
  background: radial-gradient(circle, ${blueColor}22 0%, transparent 70%);
  pointer-events: none;
`;

const BgGlowRight = styled.div`
  position: absolute;
  bottom: -100px;
  right: -100px;
  width: 400px;
  height: 400px;
  border-radius: 50%;
  background: radial-gradient(circle, ${goldColor}14 0%, transparent 70%);
  pointer-events: none;
`;

const Inner = styled.div`
  display: grid;
  grid-template-columns: 1fr 280px 1fr;
  gap: 48px;
  align-items: center;
  padding: 0 20px;
  position: relative;
  z-index: 1;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr 240px;

    & > :last-child {
      display: none;
    }
  }

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
    gap: 36px;

    & > :nth-child(2) {
      order: -1;
      max-width: 260px;
      margin: 0 auto;
    }
  }
`;

/* Left col */
const LeftCol = styled.div``;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.2px;
  text-transform: uppercase;
  color: ${goldColor};
  background: ${goldColor}18;
  border: 1px solid ${goldColor}35;
  border-radius: 20px;
  padding: 4px 14px;
  margin-bottom: 20px;
`;

const EyebrowDot = styled.span`
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: ${goldColor};
`;

const Headline = styled.h2`
  font-size: 42px;
  font-weight: 900;
  color: #ffffff;
  letter-spacing: -1.5px;
  line-height: 1.1;
  margin: 0 0 16px;

  @media (max-width: 768px) {
    font-size: 30px;
  }
`;

const AccentGold = styled.span`
  color: ${goldColor};
`;

const Subline = styled.p`
  font-size: 14.5px;
  color: rgba(255, 255, 255, 0.52);
  line-height: 1.7;
  margin: 0 0 28px;
  max-width: 420px;
`;

const FeatureRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 32px;
`;

const FeaturePill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 20px;
  padding: 5px 13px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.7);

  .anticon {
    color: ${goldColor};
    font-size: 13px;
  }
`;

/* Store buttons */
const StoreButtons = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 24px;
`;

const StoreBtn = styled.a<{ $primary?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  background: ${(p) => (p.$primary ? "#fff" : "rgba(255,255,255,0.08)")};
  border: 1.5px solid ${(p) => (p.$primary ? "#fff" : "rgba(255,255,255,0.18)")};
  border-radius: 14px;
  padding: 11px 20px;
  text-decoration: none;
  transition: all 0.22s;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(0, 0, 0, 0.25);
    background: ${(p) => (p.$primary ? "#f1f5f9" : "rgba(255,255,255,0.13)")};
  }
`;

const StoreBtnIcon = styled.div`
  color: ${blueColor};
  display: flex;
  align-items: center;
`;

const StoreBtnText = styled.div``;

const StoreBtnSub = styled.div`
  font-size: 9.5px;
  color: #64748b;
  font-weight: 500;
  letter-spacing: 0.3px;
  line-height: 1;

  ${StoreBtn}:not([style*="background: rgba"]) & {
    color: #64748b;
  }
`;

const StoreBtnMain = styled.div`
  font-size: 15px;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.3px;
  line-height: 1.2;

  /* For dark btn */
  ${StoreBtn}:not(:first-child) & {
    color: #fff;
  }
`;

/* Rating */
const RatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const RatingStars = styled.div`
  display: flex;
  gap: 2px;
`;

const RatingText = styled.div`
  display: flex;
  align-items: baseline;
  gap: 4px;
`;

const RatingScore = styled.div`
  font-size: 16px;
  font-weight: 900;
  color: #fff;
`;

const RatingCount = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.42);
`;

const RatingDivider = styled.div`
  width: 1px;
  height: 18px;
  background: rgba(255, 255, 255, 0.15);
`;

const RatingBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
`;

/* Phone */
const PhoneCol = styled.div`
  display: flex;
  justify-content: center;
  position: relative;
`;

const PhoneGlow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 260px;
  height: 260px;
  border-radius: 50%;
  background: radial-gradient(circle, ${blueColor}40 0%, transparent 70%);
  pointer-events: none;
  z-index: 0;
`;

const PhoneFrame = styled.div`
  width: 240px;
  background: #0f172a;
  border-radius: 36px;
  border: 6px solid #1e293b;
  box-shadow:
    0 0 0 1px #334155,
    0 40px 80px rgba(0, 0, 0, 0.5),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  position: relative;
  z-index: 1;
  animation: ${floatPhone} 4s ease-in-out infinite;
  overflow: hidden;
`;

const PhoneNotch = styled.div`
  width: 80px;
  height: 22px;
  background: #0f172a;
  border-radius: 0 0 16px 16px;
  margin: 0 auto 0;
  position: relative;
  z-index: 2;
`;

const PhoneScreen = styled.div`
  background: #f8fafc;
  padding: 12px 14px 20px;
  min-height: 460px;
`;

const AppHeader = styled.div`
  margin-bottom: 14px;
`;

const AppHeaderGreet = styled.div`
  font-size: 10px;
  color: #94a3b8;
`;

const AppHeaderName = styled.div`
  font-size: 14px;
  font-weight: 800;
  color: #0f172a;
`;

const AppBalance = styled.div`
  background: linear-gradient(135deg, ${blueColor} 0%, #3b82f6 100%);
  border-radius: 14px;
  padding: 14px 16px;
  margin-bottom: 14px;
`;

const AppBalanceLabel = styled.div`
  font-size: 9px;
  color: rgba(255, 255, 255, 0.65);
  text-transform: uppercase;
  letter-spacing: 0.7px;
  margin-bottom: 4px;
`;

const AppBalanceAmt = styled.div`
  font-size: 20px;
  font-weight: 900;
  color: #fff;
  letter-spacing: -0.5px;
`;

const AppActions = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 6px;
  margin-bottom: 14px;
`;

const AppAction = styled.div`
  background: #fff;
  border-radius: 10px;
  padding: 8px 4px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  border: 1px solid #e8f0f8;
`;

const AppActionDot = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 6px;
  background: ${blueColor}18;
`;

const AppActionLabel = styled.div`
  font-size: 8px;
  color: #64748b;
  font-weight: 600;
  text-align: center;
`;

const AppTxTitle = styled.div`
  font-size: 10px;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AppTx = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fff;
  border-radius: 10px;
  padding: 9px 10px;
  margin-bottom: 6px;
  border: 1px solid #f1f5f9;
  border-left: 2px solid ${(p) => p.$color};
`;

const AppTxIcon = styled.div`
  font-size: 14px;
  flex-shrink: 0;
`;

const AppTxInfo = styled.div`
  flex: 1;
`;

const AppTxName = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  color: #0f172a;
`;

const AppTxDate = styled.div`
  font-size: 9px;
  color: #94a3b8;
`;

const AppTxAmt = styled.div<{ $out?: boolean }>`
  font-size: 11px;
  font-weight: 800;
  color: ${(p) => (p.$out ? "#ef4444" : "#22c55e")};
`;

/* Floating notification */
const PhoneNotifCard = styled.div`
  position: absolute;
  bottom: -10px;
  right: -32px;
  background: rgba(255, 255, 255, 0.97);
  backdrop-filter: blur(12px);
  border-radius: 14px;
  padding: 10px 14px;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.22);
  border: 1px solid rgba(255, 255, 255, 0.9);
  animation: ${fadeSlideIn} 0.6s ease both;
  animation-delay: 1.2s;

  @media (max-width: 760px) {
    right: -8px;
  }
`;

const NotifDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
`;

const NotifContent = styled.div``;

const NotifTitle = styled.div`
  font-size: 12px;
  font-weight: 700;
  color: #0f172a;
`;

const NotifSub = styled.div`
  font-size: 10.5px;
  color: #64748b;
`;

const NotifCheck = styled.div`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #22c55e;
  color: #fff;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
`;

/* Right reviews */
const ReviewCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ReviewColLabel = styled.div`
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 1.1px;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.35);
  margin-bottom: 4px;
`;

const ReviewCard = styled.div`
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  padding: 16px 18px;
  transition: all 0.22s;
  cursor: default;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: ${goldColor}40;
  }
`;

const ReviewStars = styled.div`
  display: flex;
  gap: 2px;
  margin-bottom: 8px;
`;

const ReviewText = styled.div`
  font-size: 13.5px;
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.55;
  font-style: italic;
  margin-bottom: 8px;
`;

const ReviewName = styled.div`
  font-size: 11.5px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.4);
  letter-spacing: 0.3px;
`;

/* USSD */
const USSDCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${goldColor}14;
  border: 1px solid ${goldColor}30;
  border-radius: 14px;
  padding: 14px 16px;
  margin-top: 4px;
`;

const USSDIcon = styled.div`
  font-size: 22px;
  flex-shrink: 0;
`;

const USSDText = styled.div``;

const USSDTitle = styled.div`
  font-size: 13px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 3px;
`;

const USSDSub = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
`;

const USSDCode = styled.span`
  font-family: monospace;
  font-weight: 700;
  color: ${goldColor};
  font-size: 13px;
`;
